import { useState } from "react";
import { BookOpen, Clock3, PenLine } from "lucide-react";
import { Reveal } from "./motion";

export function OssdPlanner() {
  const [credits, setCredits] = useState(16);
  const [hours, setHours] = useState(15);
  const [literacy, setLiteracy] = useState(false);

  const creditsPct = (credits / 30) * 100;
  const hoursPct = (hours / 40) * 100;
  const remainCredits = Math.max(0, 30 - credits);
  const remainHours = Math.max(0, 40 - hours);
  const done = remainCredits === 0 && remainHours === 0 && literacy;

  return (
    <div className="card-line p-6 sm:p-10" data-testid="ossd-planner-widget">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-xl font-bold text-ink sm:text-2xl">Plan your diploma in 30 seconds</h3>
        <span className={`rounded-full px-3 py-1 font-mono text-xs font-bold uppercase tracking-widest ${done ? "bg-aqualight text-teal-800" : "bg-brandlight text-amber-800"}`}>
          {done ? "Graduation ready" : "In progress"}
        </span>
      </div>

      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <div>
          <div className="flex items-baseline justify-between">
            <label className="field-label !mb-0">Credits earned toward 30</label>
            <span className="font-mono text-lg font-bold text-ink" data-testid="ossd-credits-value">{credits}/30</span>
          </div>
          <input
            type="range" min="0" max="30" value={credits}
            onChange={(e) => setCredits(Number(e.target.value))}
            style={{ "--fill": `${creditsPct}%` }}
            className="mt-4" data-testid="ossd-credits-slider"
            aria-label="Credits earned"
          />
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-ink transition-all duration-300" style={{ width: `${creditsPct}%` }} />
          </div>
          <p className="mt-2 text-sm text-slate-500" data-testid="ossd-credits-remaining">
            {remainCredits === 0 ? "All 30 credits complete." : `${remainCredits} credit${remainCredits === 1 ? "" : "s"} to go — we offer all of them online.`}
          </p>
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <label className="field-label !mb-0">Community hours logged of 40</label>
            <span className="font-mono text-lg font-bold text-ink" data-testid="ossd-hours-value">{hours}/40</span>
          </div>
          <input
            type="range" min="0" max="40" value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            style={{ "--fill": `${hoursPct}%` }}
            className="mt-4" data-testid="ossd-hours-slider"
            aria-label="Community hours"
          />
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-aqua transition-all duration-300" style={{ width: `${hoursPct}%` }} />
          </div>
          <p className="mt-2 text-sm text-slate-500" data-testid="ossd-hours-remaining">
            {remainHours === 0 ? "Community involvement complete." : `${remainHours} hour${remainHours === 1 ? "" : "s"} remaining — any volunteer role counts.`}
          </p>
        </div>
      </div>

      <label className="mt-8 flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-sand/60 p-4" data-testid="ossd-literacy-row">
        <input
          type="checkbox" checked={literacy} onChange={(e) => setLiteracy(e.target.checked)}
          data-testid="ossd-literacy-check" className="h-5 w-5 accent-[#D97706]"
        />
        <span className="text-sm font-semibold text-ink">
          I have met the provincial literacy requirement (OSSLT or the literacy course)
        </span>
      </label>

      <div className="mt-8 flex flex-wrap gap-4">
        <a href="/admissions" data-testid="ossd-planner-apply" className="btn-accent">Start your <span className="text-ink">next credit</span></a>
        <a href="/courses" data-testid="ossd-planner-browse" className="btn-ghost">Browse <span className="text-brand">courses</span></a>
      </div>
    </div>
  );
}

export function OssdPillars() {
  const pillars = [
    { icon: BookOpen, num: "30", title: "Credits", text: "17 compulsory + 13 elective credits for students who started Grade 9 in Fall 2024 or after — all available online with us, including every university prerequisite." },
    { icon: Clock3, num: "40", title: "Community hours", text: "Volunteer hours you arrange anywhere, any time — we track and validate them on your transcript." },
    { icon: PenLine, num: "1", title: "Literacy requirement", text: "The OSSLT (or the literacy course), written online with full preparation built into our curriculum." },
  ];
  const tones = ["text-brand", "text-cyan-600", "text-violet-600"];
  return (
    <div className="grid gap-5 md:grid-cols-3">
      {pillars.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.08} className="h-full">
          <div className="card-line h-full p-7">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${["bg-brandlight", "bg-aqualight", "bg-grapelight"][i]}`}>
              <p.icon className={`h-5 w-5 ${tones[i]}`} />
            </span>
            <p className="mt-5 font-mono text-4xl font-bold text-ink">{p.num}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-ink">{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}
